import React from 'react';
import {CheckCircle2, Circle, TrendingUp} from 'lucide-react';
import {AdoptionMilestoneKey, OrganisationAdoption as OrganisationAdoptionData} from '../../types/__generated__/graphql';

/** Funnel order and admin-facing labels. The server returns one milestone per key in no guaranteed order. */
export const ADOPTION_MILESTONES: {key: AdoptionMilestoneKey; label: string}[] = [
  {key: AdoptionMilestoneKey.InvitedUser, label: 'Invited a user'},
  {key: AdoptionMilestoneKey.CreatedProject, label: 'Created a project'},
  {key: AdoptionMilestoneKey.CreatedArtwork, label: 'Created an artwork'},
  {key: AdoptionMilestoneKey.UploadedRevision, label: 'Uploaded a revision'},
  {key: AdoptionMilestoneKey.CommentedArtwork, label: 'Commented on an artwork'},
  {key: AdoptionMilestoneKey.AssignedApprover, label: 'Assigned an approver'},
  {key: AdoptionMilestoneKey.ApprovedArtwork, label: 'Approved an artwork'},
  {key: AdoptionMilestoneKey.RejectedArtwork, label: 'Rejected an artwork'},
  {key: AdoptionMilestoneKey.DeliveredArtwork, label: 'Delivered an artwork'},
  {key: AdoptionMilestoneKey.ClosedProject, label: 'Closed a project'},
  {key: AdoptionMilestoneKey.UploadedToBrandStudio, label: 'Uploaded to Brand Studio'},
  {key: AdoptionMilestoneKey.SyncedToBrandStudio, label: 'Synced to Brand Studio'},
  {key: AdoptionMilestoneKey.CreatedShare, label: 'Created a share'},
  {key: AdoptionMilestoneKey.ShareDownloaded, label: 'Share downloaded'},
];

interface OrganisationAdoptionProps {
  /** `undefined` while loading; `null` when the server withheld the field (not a system admin, or a resolver error). */
  adoption: OrganisationAdoptionData | null | undefined;
  loading?: boolean;
}

const formatDate = (timestamp: number | null | undefined) =>
  timestamp ? new Date(timestamp).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'}) : null;

export function OrganisationAdoption({adoption, loading = false}: OrganisationAdoptionProps) {
  const byKey = new Map(adoption?.milestones.map((milestone) => [milestone.key, milestone]) ?? []);
  const achievedCount = ADOPTION_MILESTONES.filter(({key}) => byKey.get(key)?.achieved).length;
  const lastActivity = formatDate(adoption?.lastActivityAt);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <TrendingUp size={20} className="mr-2" />
            Adoption
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Funnel steps this organisation has reached, for trial follow-up.</p>
        </div>
        {adoption && (
          <div className="text-right shrink-0">
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {achievedCount}
              <span className="text-base font-medium text-gray-500 dark:text-gray-400"> / {ADOPTION_MILESTONES.length}</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {lastActivity ? `Last activity ${lastActivity}` : 'No tracked activity yet'}
            </p>
          </div>
        )}
      </div>

      {loading && !adoption ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading adoption data…</p>
      ) : !adoption ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Adoption data is not available for this organisation.</p>
      ) : (
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {ADOPTION_MILESTONES.map(({key, label}) => {
            const milestone = byKey.get(key);
            const achieved = milestone?.achieved === true;
            const firstAchieved = formatDate(milestone?.firstAchievedAt);
            return (
              <li
                key={key}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                  achieved
                    ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30'
                }`}
              >
                {achieved ? (
                  <CheckCircle2 size={18} className="text-green-600 dark:text-green-400 shrink-0" aria-label="Reached" />
                ) : (
                  <Circle size={18} className="text-gray-300 dark:text-gray-600 shrink-0" aria-label="Not reached" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${achieved ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                    {label}
                  </p>
                  {achieved && firstAchieved && <p className="text-xs text-gray-500 dark:text-gray-400">First on {firstAchieved}</p>}
                </div>
                {milestone && milestone.count > 0 && (
                  <span className="text-xs font-medium tabular-nums text-gray-600 dark:text-gray-300" title="Times reached">
                    ×{milestone.count}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
